import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlay, FaArrowRight, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

/**
 * TacoStudioWelcomeSplash Component
 * 
 * Displays a welcoming, inspirational interface when the studio is empty.
 * Shows curated examples of what's possible and guides users to start creating.
 * 
 * Features:
 * - Desktop: Grid gallery of examples with quick start guide
 * - Mobile: Swipeable carousel with hero image
 * - Interactive examples that load prompts/settings
 */

// Curated examples showcasing different capabilities
const EXAMPLE_GALLERY = [
  {
    id: 1,
    title: "Product Photography",
    description: "Professional product shots",
    imageUrl: "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&q=80",
    prompt: "Professional product photography of elegant watch on marble surface, studio lighting, high-end commercial style, 4K quality",
    model: "flux-pro",
    aspectRatio: "Square"
  },
  {
    id: 2,
    title: "Fashion Editorial",
    description: "High-fashion creative shots",
    imageUrl: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80",
    prompt: "Editorial fashion photography, model in flowing dress, dramatic lighting, high fashion magazine style, Vogue aesthetic",
    model: "flux-pro",
    aspectRatio: "Portrait"
  },
  {
    id: 3,
    title: "AI Art Concepts",
    description: "Creative digital art",
    imageUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=800&q=80",
    prompt: "Abstract digital art, vibrant colors, geometric patterns, modern contemporary style, artistic composition",
    model: "free",
    aspectRatio: "Landscape"
  },
  {
    id: 4,
    title: "Lifestyle Content",
    description: "Social media ready images",
    imageUrl: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&q=80",
    prompt: "Lifestyle flat lay photography, minimal aesthetic, pastel colors, Instagram-worthy composition, soft natural lighting",
    model: "flux-pro",
    aspectRatio: "Square"
  }
];

const TacoStudioWelcomeSplash = ({ 
  onExampleSelect,
  onStartCreating,
  containerHeight 
}) => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const [hoveredExample, setHoveredExample] = useState(null);

  // Handle example selection - loads prompt and settings into editor
  const handleExampleClick = (example) => {
    if (onExampleSelect) {
      onExampleSelect({
        prompt: example.prompt,
        model: example.model,
        aspectRatio: example.aspectRatio
      });
    }
  };

  // Mobile carousel navigation
  const handlePrevSlide = () => {
    setCurrentMobileIndex((prev) => 
      prev === 0 ? EXAMPLE_GALLERY.length - 1 : prev - 1
    );
  };

  const handleNextSlide = () => {
    setCurrentMobileIndex((prev) => 
      prev === EXAMPLE_GALLERY.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <div 
      className="w-full h-full flex items-center justify-center"
      style={{ minHeight: containerHeight ? `${containerHeight}px` : '500px' }}
    >
      {/* Desktop Layout */}
      <div className="hidden lg:flex w-full h-full max-w-6xl mx-auto px-8 py-12">
        <div className="w-full flex flex-col items-center justify-center space-y-8">
          
          {/* Header Section */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-4"
          >
            {/* <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full backdrop-blur-sm">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-gray-300">Prototype Studio</span>
            </div> */}
            
            <h1 className="text-4xl md:text-5xl font-bold">
              <span className="gradient-text">Unleash Your Creativity</span>
            </h1>
            
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Generate stunning images and videos with AI. Choose from powerful models, 
              customize settings, and bring your vision to life.
            </p>
          </motion.div>

          {/* Example Gallery Grid */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full max-w-5xl"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {EXAMPLE_GALLERY.map((example, index) => (
                <motion.div
                  key={example.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  className="relative group cursor-pointer"
                  onMouseEnter={() => setHoveredExample(example.id)}
                  onMouseLeave={() => setHoveredExample(null)}
                  // onClick={() => handleExampleClick(example)}
                >
                  {/* Example Image */}
                  <div className="relative aspect-square rounded-xl overflow-hidden border border-white/10 bg-black/20">
                    <img 
                      src={example.imageUrl} 
                      alt={example.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    
                    {/* Overlay on Hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                      <h3 className="text-white font-semibold text-sm mb-1">
                        {example.title}
                      </h3>
                      <p className="text-gray-300 text-xs mb-3">
                        {example.description}
                      </p>
                      {/* <button className="w-full py-2 px-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg text-white text-xs font-medium flex items-center justify-center gap-2 hover:from-purple-600 hover:to-pink-600 transition-all">
                        <FaPlay className="text-xs" />
                        Generate This
                      </button> */}
                    </div>
                    
                    {/* Badge */}
                    {/* <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-white border border-white/20">
                      {example.model === 'free' ? 'Free' : 'Pro'}
                    </div> */}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Quick Start Guide */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="w-full max-w-3xl"
          >
            <div className="glass-effect border border-white/10 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4 text-center">
                Quick Start Guide
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Step 1 */}
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                    1
                  </div>
                  <h3 className="text-white font-medium">Choose Model</h3>
                  <p className="text-gray-400 text-sm">
                    Select from free or pro models in the right panel
                  </p>
                </div>
                
                {/* Step 2 */}
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                    2
                  </div>
                  <h3 className="text-white font-medium">Describe Vision</h3>
                  <p className="text-gray-400 text-sm">
                    Enter your prompt and adjust settings
                  </p>
                </div>
                
                {/* Step 3 */}
                <div className="flex flex-col items-center text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-lg">
                    3
                  </div>
                  <h3 className="text-white font-medium">Generate</h3>
                  <p className="text-gray-400 text-sm">
                    Hit generate and watch magic happen!
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* CTA Button */}
          {/* <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <button
              onClick={onStartCreating}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white font-semibold text-lg flex items-center gap-3 hover:from-purple-600 hover:to-pink-600 transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Creating Now
              <FaArrowRight />
            </button>
          </motion.div> */}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="flex lg:hidden w-full h-full px-4 py-8">
        <div className="w-full flex flex-col items-center justify-center space-y-6">
          
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-2"
          >
            <h1 className="text-3xl font-bold">
              <span className="gradient-text">Prototype Studio</span>
            </h1>
            <p className="text-gray-400 text-sm">
              Tap an example to get started
            </p>
          </motion.div>

          {/* Hero Example with Carousel */}
          <div className="w-full max-w-md relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentMobileIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.3 }}
                className="relative"
                // onClick={() => handleExampleClick(EXAMPLE_GALLERY[currentMobileIndex])}
              >
                {/* Hero Image */}
                <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                  <img 
                    src={EXAMPLE_GALLERY[currentMobileIndex].imageUrl}
                    alt={EXAMPLE_GALLERY[currentMobileIndex].title}
                    className="w-full h-full object-cover"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent flex flex-col justify-end p-6">
                    <h2 className="text-white font-bold text-xl mb-2">
                      {EXAMPLE_GALLERY[currentMobileIndex].title}
                    </h2>
                    <p className="text-gray-300 text-sm mb-4">
                      {EXAMPLE_GALLERY[currentMobileIndex].description}
                    </p>
                    {/* <button className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl text-white font-semibold flex items-center justify-center gap-2">
                      <FaPlay />
                      Tap to Generate This
                    </button> */}
                  </div>
                  
                  {/* Badge */}
                  {/* <div className="absolute top-4 right-4 px-3 py-1.5 bg-black/60 backdrop-blur-sm rounded-full text-sm text-white border border-white/20">
                    {EXAMPLE_GALLERY[currentMobileIndex].model === 'free' ? 'Free' : 'Pro'}
                  </div> */}
                </div>

                {/* Navigation Arrows */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevSlide();
                  }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white border border-white/20 transition-all"
                >
                  <FaChevronLeft />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextSlide();
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center text-white border border-white/20 transition-all"
                >
                  <FaChevronRight />
                </button>
              </motion.div>
            </AnimatePresence>

            {/* Carousel Indicators */}
            <div className="flex justify-center gap-2 mt-4">
              {EXAMPLE_GALLERY.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentMobileIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentMobileIndex 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 w-8' 
                      : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Thumbnail Carousel */}
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex gap-3 pb-2">
              {EXAMPLE_GALLERY.map((example, index) => (
                <button
                  key={example.id}
                  onClick={() => {
                    setCurrentMobileIndex(index);
                    handleExampleClick(example);
                  }}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentMobileIndex 
                      ? 'border-purple-500 ring-2 ring-purple-500/50' 
                      : 'border-white/20'
                  }`}
                >
                  <img 
                    src={example.imageUrl} 
                    alt={example.title}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Quick Start */}
          <div className="w-full glass-effect border border-white/10 rounded-xl p-4">
            <h3 className="text-white font-semibold text-center mb-3">
              How It Works
            </h3>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold">1</span>
                <span>Choose a model & enter your prompt</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold">2</span>
                <span>Adjust settings to your preference</span>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-xs font-bold">3</span>
                <span>Hit generate and create magic!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TacoStudioWelcomeSplash;

